import * as React from 'react';

import type {ComparedActivityReportSummary, ReportRowData} from 'nutshell-core/types';

import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
import {ErrorState} from 'shells/error-state/error-state';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {DisplayOnlyTable} from 'shells/table/display-only-table';
import type {Column} from 'shells/table/table-types';

import styles from './report-summary-table.module.css';

type Props = {
    data: ComparedActivityReportSummary<Array<ReportRowData>> | null | undefined;
    hasError: boolean;
    isLoading: boolean;
    columns: Column<any>[];
    emptyStateText: {
        title: string;
        subtitle: string;
    };
    disableSorting?: boolean;
    isResizable?: boolean;
    shouldFooterAlign?: boolean;
    extendToFitWidth?: boolean;
};

export function ReportSummaryTable(props: Props) {
    const {isResizable = true, extendToFitWidth = true} = props;

    return (
        <div className={styles.container}>
            <DisplayOnlyTable
                isResizable={isResizable}
                error={props.hasError ? 'An unexpected error occurred' : undefined}
                isLoading={props.isLoading}
                columns={props.columns}
                data={props.data && props.data.currentPeriod ? props.data.currentPeriod : []}
                shouldShowFooter={true}
                displayFooterAtTop={true}
                disableSorting={props.disableSorting ?? false}
                extendToFitWidth={extendToFitWidth}
                sortBy={[{id: 'user', desc: false}]}
                hasCellBorders={true}
                hasFooterCellBorders={true}
                shouldFooterAlign={props.shouldFooterAlign}
                specialStates={[
                    {
                        // Error state
                        shouldRender: () => props.hasError,
                        component: <ErrorState errorMessage='An unexpected error occurred' />,
                    },
                    {
                        // Loading state
                        shouldRender: () =>
                            props.isLoading &&
                            (!props.data ||
                                !props.data.currentPeriod ||
                                props.data.currentPeriod.length === 0),
                        component: <LoadingPage />,
                    },
                    {
                        // Empty state
                        shouldRender: () =>
                            !props.hasError &&
                            !props.isLoading &&
                            (!props.data ||
                                !props.data.currentPeriod ||
                                props.data.currentPeriod.length === 0),
                        component: (
                            <div className={styles['empty-state']}>
                                <NothingFoundEmptyState {...props.emptyStateText} />
                            </div>
                        ),
                    },
                ]}
            />
        </div>
    );
}
